import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { formatIndianDate, formatIndianDateOnly, getStatusColorClass } from '../utils/formatters';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { Plus, Edit, Search, FileText, Check, ChevronsUpDown, AlertTriangle, Wrench, ExternalLink, Download } from 'lucide-react';
import useProtectedExport from '../hooks/useProtectedExport';
import ExportPasswordDialog from '../components/ExportPasswordDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EXPORT_ALLOWED_ROLES = ["Admin"];

const ContractsManager = () => {
  const { user } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [createServiceDialogOpen, setCreateServiceDialogOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [renewalFilter, setRenewalFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Check if user can export
  const canExport = EXPORT_ALLOWED_ROLES.includes(user?.role);
  const protectedExport = useProtectedExport();
  
  // Export CSV handler
  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status_filter', statusFilter);
      if (typeFilter !== 'all') params.append('contract_type', typeFilter);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/contracts/export/csv?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contracts_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('CSV exported successfully');
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };
  
  // Service creation form
  const [serviceForm, setServiceForm] = useState({
    customer_id: '',
    service_type: 'AMC/MMC Service',
    issue_description: '',
    priority: 'Medium',
    assigned_to: '',
    due_date: ''
  });

  const [formData, setFormData] = useState({
    customer_id: '',
    machine_device: '',
    contract_type: 'AMC',
    start_date: '',
    end_date: '',
    monthly_service_day: '',
    services_included: '',
    services_used: 0,
    quantity: '',
    amount: '',
    contract_status: 'Active'
  });

  // Check URL params for filters on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const type = params.get('type');
    const renewal = params.get('renewal');
    if (type) setTypeFilter(type);
    if (renewal) setRenewalFilter(renewal);
  }, []);

  useEffect(() => {
    fetchContracts();
    fetchCustomers();
    fetchEngineers();
  }, []);

  const fetchContracts = async () => {
    setLoading(true);
    try {
      const response = await api.get('/contracts/');
      const sortedContracts = response.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setContracts(sortedContracts);
    } catch (error) {
      toast.error('Failed to fetch contracts');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers/');
      setCustomers(response.data.filter(c => c.status === 'Active'));
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  };

  const fetchEngineers = async () => {
    try {
      // Use the new assignable services endpoint
      const response = await api.get('/employees/assignable/services');
      setEngineers(response.data);
    } catch (error) {
      console.error('Failed to fetch engineers from assignable:', error);
      // Fallback to regular employees endpoint for Admin
      try {
        const fallbackResponse = await api.get('/employees/');
        const engineersList = fallbackResponse.data.filter(e => 
          e.is_active !== false && 
          (e.role === 'Service Engineer' || e.role === 'BDM & SE')
        );
        setEngineers(engineersList);
      } catch (e) {
        console.error('Fallback failed:', e);
      }
    }
  };

  const handleOpenDialog = (contract = null) => {
    if (contract) {
      setSelectedContract(contract);
      setFormData({
        customer_id: contract.customer_id,
        machine_device: contract.machine_device,
        contract_type: contract.contract_type,
        start_date: contract.start_date || '',
        end_date: contract.end_date || '',
        monthly_service_day: contract.monthly_service_day || '',
        services_included: contract.services_included,
        services_used: contract.services_used || 0,
        quantity: contract.quantity || '',
        amount: contract.amount || '',
        contract_status: contract.contract_status
      });
    } else {
      setSelectedContract(null);
      setFormData({
        customer_id: '',
        machine_device: '',
        contract_type: 'AMC',
        start_date: '',
        end_date: '',
        monthly_service_day: '',
        services_included: '',
        services_used: 0,
        quantity: '',
        amount: '',
        contract_status: 'Active'
      });
    }
    setCustomerSearchTerm('');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedContract(null);
    setCustomerSearchTerm('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Prepare data for submission
      const submitData = {
        ...formData,
        services_included: parseInt(formData.services_included) || 0,
        services_used: parseInt(formData.services_used) || 0,
        quantity: formData.quantity ? parseInt(formData.quantity) : null,
        amount: formData.amount ? parseFloat(formData.amount) : null,
        monthly_service_day: formData.contract_type === 'MMC' ? parseInt(formData.monthly_service_day) || null : null
      };

      if (selectedContract) {
        await api.put(`/contracts/${selectedContract.id}`, submitData);
        toast.success('Contract updated successfully');
      } else {
        await api.post('/contracts/', submitData);
        toast.success('Contract created successfully');
      }
      handleCloseDialog();
      fetchContracts();
    } catch (error) {
      // Handle validation errors properly
      const errorData = error.response?.data;
      let errorMessage = 'Operation failed';
      
      if (errorData?.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          // Pydantic validation errors come as array
          errorMessage = errorData.detail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
        }
      }
      toast.error(errorMessage);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to check if contract is expiring within given days
  const isExpiringWithinDays = (endDate, days) => {
    if (!endDate) return false;
    const today = new Date();
    const expiry = new Date(endDate);
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry >= 0 && daysUntilExpiry <= days;
  };

  // Helper function to check if MMC service day is within next N days
  const isMMCServiceWithinDays = (serviceDay, days) => {
    if (!serviceDay) return false;
    const today = new Date();
    const currentDay = today.getDate();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    
    // Get next service date
    let nextServiceDate;
    if (serviceDay >= currentDay) {
      // Service day is later this month
      nextServiceDate = new Date(currentYear, currentMonth, serviceDay);
    } else {
      // Service day is next month
      nextServiceDate = new Date(currentYear, currentMonth + 1, serviceDay);
    }
    
    const daysUntilService = Math.ceil((nextServiceDate - today) / (1000 * 60 * 60 * 24));
    return daysUntilService >= 0 && daysUntilService <= days;
  };

  // Get days until expiry (negative if expired) for AMC
  const getDaysUntilExpiry = (endDate) => {
    if (!endDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(endDate);
    expiry.setHours(0, 0, 0, 0);
    return Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
  };

  // Check if contract needs service (expiring soon or expired)
  const needsService = (contract) => {
    if (contract.contract_type === 'AMC') {
      const daysLeft = getDaysUntilExpiry(contract.end_date);
      return daysLeft !== null && daysLeft <= 90;
    } else if (contract.contract_type === 'MMC') {
      return isMMCServiceWithinDays(contract.monthly_service_day, 7);
    }
    return false;
  };

  // Open create service dialog from contract
  const handleOpenCreateService = (contract) => {
    setSelectedContract(contract);
    
    let daysInfo = '';
    let priority = 'Medium';
    
    if (contract.contract_type === 'AMC') {
      const daysLeft = getDaysUntilExpiry(contract.end_date);
      if (daysLeft !== null) {
        daysInfo = daysLeft < 0 ? `${Math.abs(daysLeft)} days overdue` : `${daysLeft} days left`;
        if (daysLeft <= 30) priority = 'High';
        if (daysLeft <= 0) priority = 'Critical';
      }
    } else {
      daysInfo = `Monthly service (Day ${contract.monthly_service_day})`;
      priority = 'Medium';
    }
    
    // Pre-fill service form with contract data
    setServiceForm({
      customer_id: contract.customer_id || '',
      service_type: 'AMC/MMC Service',
      issue_description: `${contract.contract_type} Contract Renewal/Service\n\nContract No: ${contract.contract_no}\nMachine/Device: ${contract.machine_device}\nContract Type: ${contract.contract_type}${contract.contract_type === 'AMC' ? `\nEnd Date: ${formatIndianDateOnly(contract.end_date)} (${daysInfo})` : `\nMonthly Service Day: ${contract.monthly_service_day}`}\nServices Used: ${contract.services_used || 0} / ${contract.services_included}\n\nCustomer: ${contract.customer?.company_name || 'N/A'}`,
      priority: priority,
      assigned_to: '',
      due_date: contract.contract_type === 'AMC' 
        ? (contract.end_date || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0])
        : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    });
    
    setCreateServiceDialogOpen(true);
  };

  // Create service from contract
  const handleCreateService = async () => {
    if (!serviceForm.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    if (!serviceForm.issue_description) {
      toast.error('Issue description is required');
      return;
    }
    if (!serviceForm.assigned_to) {
      toast.error('Please assign a service engineer');
      return;
    }

    setSubmitting(true);
    try {
      // Create the service with contract_id
      const response = await api.post('/services/', {
        customer_id: serviceForm.customer_id,
        service_type: serviceForm.service_type,
        description: serviceForm.issue_description,
        priority: serviceForm.priority,
        assigned_to: serviceForm.assigned_to,
        due_date: serviceForm.due_date || null,
        contract_id: selectedContract?.id || null
      });

      const createdService = response.data;
      
      toast.success(`Service ${createdService.service_no} created for ${selectedContract.contract_type} contract`);
      setCreateServiceDialogOpen(false);
      fetchContracts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create service');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredContracts = contracts.filter(contract => {
    const contractNo = contract.contract_no || '';
    const companyName = contract.customer?.company_name || '';
    const customerMobile = contract.customer?.mobile || '';
    const machineDevice = contract.machine_device || '';
    const searchLower = searchTerm.toLowerCase();
    
    const matchesSearch = 
      contractNo.toLowerCase().includes(searchLower) ||
      companyName.toLowerCase().includes(searchLower) ||
      customerMobile.includes(searchTerm) ||
      machineDevice.toLowerCase().includes(searchLower);
    
    const matchesStatus = statusFilter === 'all' || contract.contract_status === statusFilter;
    
    // Contract type filter
    const matchesType = typeFilter === 'all' || contract.contract_type === typeFilter;
    
    // Renewal filter for AMC contracts or MMC upcoming service
    let matchesRenewal = true;
    if (renewalFilter !== 'all') {
      const days = parseInt(renewalFilter);
      if (contract.contract_type === 'AMC' && contract.end_date) {
        matchesRenewal = isExpiringWithinDays(contract.end_date, days);
      } else if (contract.contract_type === 'MMC' && contract.monthly_service_day) {
        matchesRenewal = isMMCServiceWithinDays(contract.monthly_service_day, days);
      } else {
        matchesRenewal = false;
      }
    }
    
    return matchesSearch && matchesStatus && matchesType && matchesRenewal;
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">AMC / MMC Contracts</h1>
          <p className="text-gray-600 mt-1">Manage maintenance contracts and service agreements</p>
        </div>
        <div className="flex gap-2">
          {canExport && (
            <Button variant="outline" onClick={() => protectedExport.requestExport(handleExportCSV)} data-testid="export-csv-button">
              <Download size={20} className="mr-2" />
              Export CSV
            </Button>
          )}
          <Button onClick={() => handleOpenDialog()} data-testid="add-contract-button">
            <Plus size={20} className="mr-2" />
            Add Contract
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Contracts</CardTitle>
          <CardDescription>View and manage all contracts</CardDescription>
          <div className="flex flex-wrap gap-3 mt-4">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search contract, customer, mobile..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="AMC">AMC Only</SelectItem>
                <SelectItem value="MMC">MMC Only</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Expired">Expired</SelectItem>
              </SelectContent>
            </Select>
            <Select value={renewalFilter} onValueChange={setRenewalFilter}>
              <SelectTrigger className="w-44">
                <SelectValue placeholder="Upcoming" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Dates</SelectItem>
                <SelectItem value="3">Next 3 Days</SelectItem>
                <SelectItem value="7">Next 7 Days</SelectItem>
                <SelectItem value="15">Next 15 Days</SelectItem>
                <SelectItem value="30">Next 30 Days</SelectItem>
                <SelectItem value="60">Next 60 Days</SelectItem>
                <SelectItem value="90">Next 90 Days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading && !contracts.length ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : filteredContracts.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText size={48} className="mx-auto mb-4 text-gray-300" />
              <p>No contracts found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Contract No.</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Mobile</TableHead>
                    <TableHead>Machine/Device</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Details</TableHead>
                    <TableHead>Services</TableHead>
                    <TableHead>Qty</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredContracts.map((contract) => (
                    <TableRow 
                      key={contract.id}
                      className={contract.contract_type === 'MMC' && isMMCServiceWithinDays(contract.monthly_service_day, 7) 
                        ? 'bg-purple-50 hover:bg-purple-100' 
                        : contract.contract_type === 'MMC' 
                          ? 'bg-blue-50 hover:bg-blue-100'
                          : ''
                      }
                    >
                      <TableCell className="font-medium">{contract.contract_no}</TableCell>
                      <TableCell>{contract.customer?.company_name || 'N/A'}</TableCell>
                      <TableCell>
                        {contract.customer?.mobile ? (
                          <a href={`tel:${contract.customer.mobile}`} className="text-blue-600 hover:underline">
                            {contract.customer.mobile}
                          </a>
                        ) : 'N/A'}
                      </TableCell>
                      <TableCell>{contract.machine_device}</TableCell>
                      <TableCell>
                        <Badge 
                          variant="outline" 
                          className={contract.contract_type === 'MMC' ? 'bg-blue-100 text-blue-800 border-blue-300' : ''}
                        >
                          {contract.contract_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {contract.contract_type === 'AMC' ? (
                          <div>
                            <span className="text-gray-600">Start:</span> {formatIndianDateOnly(contract.start_date)}
                            <br />
                            <div className="flex items-center gap-1">
                              <span className="text-gray-600">End:</span> {formatIndianDateOnly(contract.end_date)}
                              {isExpiringWithinDays(contract.end_date, 30) && (
                                <AlertTriangle size={14} className="text-orange-500" title="Expiring Soon" />
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <span className="text-gray-600">Service Day:</span> 
                            <span className={isMMCServiceWithinDays(contract.monthly_service_day, 7) ? 'text-purple-700 font-semibold' : ''}>
                              {contract.monthly_service_day}
                            </span>
                            <span className="text-gray-600"> of month</span>
                            {isMMCServiceWithinDays(contract.monthly_service_day, 7) && (
                              <AlertTriangle size={14} className="text-purple-600" title="Service Due Soon" />
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className={contract.services_used >= contract.services_included ? 'text-red-600 font-medium' : ''}>
                          {contract.services_used || 0} / {contract.services_included}
                        </span>
                      </TableCell>
                      <TableCell>{contract.quantity || '-'}</TableCell>
                      <TableCell>{contract.amount ? `₹${Number(contract.amount).toLocaleString('en-IN')}` : '-'}</TableCell>
                      <TableCell>
                        <Badge 
                          variant={contract.contract_status === 'Active' ? 'default' : 'destructive'}
                          className={getStatusColorClass(contract.contract_status)}
                        >
                          {contract.contract_status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{formatIndianDate(contract.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {/* Show Create Service button for contracts expiring within 90 days or MMC due within 7 days */}
                          {needsService(contract) && contract.contract_status === 'Active' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleOpenCreateService(contract)}
                              className="text-blue-600 hover:bg-blue-50"
                              title="Create Service Request"
                              data-testid={`create-service-btn-${contract.id}`}
                            >
                              <Wrench size={14} className="mr-1" />
                              Service
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDialog(contract)}
                            data-testid={`edit-contract-btn-${contract.id}`}
                          >
                            <Edit size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedContract ? 'Edit Contract' : 'Add New Contract'}</DialogTitle>
            <DialogDescription>
              {selectedContract ? 'Update contract information' : 'Create a new AMC or MMC contract'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2 col-span-2">
                <Label htmlFor="customer_id">Search Customer *</Label>
                <Popover open={customerSearchOpen} onOpenChange={setCustomerSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={customerSearchOpen}
                      className="w-full justify-between"
                    >
                      {formData.customer_id
                        ? customers.find((customer) => customer.id === formData.customer_id)?.company_name
                        : "Search and select customer..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[600px] p-0">
                    <Command shouldFilter={false}>
                      <CommandInput 
                        placeholder="Search customer..." 
                        value={customerSearchTerm}
                        onValueChange={setCustomerSearchTerm}
                      />
                      <CommandEmpty>No customer found.</CommandEmpty>
                      <CommandGroup className="max-h-64 overflow-auto">
                        {customers
                          .filter(c => {
                            const searchLower = customerSearchTerm.toLowerCase();
                            const companyName = c.company_name || '';
                            const contactPerson = c.contact_person || '';
                            const mobile = c.mobile || '';
                            return companyName.toLowerCase().includes(searchLower) ||
                                   contactPerson.toLowerCase().includes(searchLower) ||
                                   mobile.includes(customerSearchTerm);
                          })
                          .map((customer) => (
                            <CommandItem
                              key={customer.id}
                              value={customer.id}
                              onSelect={() => {
                                setFormData({ ...formData, customer_id: customer.id });
                                setCustomerSearchOpen(false);
                                setCustomerSearchTerm('');
                              }}
                            >
                              <Check
                                className={`mr-2 h-4 w-4 ${
                                  formData.customer_id === customer.id ? "opacity-100" : "opacity-0"
                                }`}
                              />
                              <div className="flex flex-col">
                                <span className="font-medium">{customer.company_name}</span>
                                <span className="text-sm text-gray-500">
                                  {customer.contact_person} • {customer.mobile}
                                </span>
                              </div>
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2 col-span-2">
                <Label htmlFor="machine_device">Machine / Device Details *</Label>
                <Input
                  id="machine_device"
                  value={formData.machine_device}
                  onChange={(e) => setFormData({ ...formData, machine_device: e.target.value })}
                  required
                  placeholder="Enter machine or device name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contract_type">Contract Type *</Label>
                <Select
                  value={formData.contract_type}
                  onValueChange={(value) => setFormData({ ...formData, contract_type: value })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AMC">AMC (Annual Maintenance Contract)</SelectItem>
                    <SelectItem value="MMC">MMC (Monthly Maintenance Contract)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="contract_status">Status *</Label>
                <Select
                  value={formData.contract_status}
                  onValueChange={(value) => setFormData({ ...formData, contract_status: value })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Expired">Expired</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.contract_type === 'AMC' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="start_date">Contract Start Date *</Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end_date">Contract End Date *</Label>
                    <Input
                      id="end_date"
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      min={new Date().toISOString().split('T')[0]}
                      required
                    />
                  </div>
                </>
              )}

              {formData.contract_type === 'MMC' && (
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="monthly_service_day">Monthly Service Day (1-31) *</Label>
                  <Input
                    id="monthly_service_day"
                    type="number"
                    min="1"
                    max="31"
                    value={formData.monthly_service_day}
                    onChange={(e) => setFormData({ ...formData, monthly_service_day: e.target.value })}
                    required
                    placeholder="Enter day of month (1-31)"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="services_included">Services Included *</Label>
                <Input
                  id="services_included"
                  type="number"
                  min="1"
                  value={formData.services_included}
                  onChange={(e) => setFormData({ ...formData, services_included: e.target.value })}
                  required
                />
              </div>

              {selectedContract && (
                <div className="space-y-2">
                  <Label htmlFor="services_used">Services Used</Label>
                  <Input
                    id="services_used"
                    type="number"
                    min="0"
                    value={formData.services_used}
                    onChange={(e) => setFormData({ ...formData, services_used: e.target.value })}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="quantity">Quantity (Optional)</Label>
                <Input
                  id="quantity"
                  type="number"
                  min="1"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  placeholder="Enter quantity"
                  data-testid="contract-quantity-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="amount">Amount (Optional)</Label>
                <Input
                  id="amount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="Enter amount"
                  data-testid="contract-amount-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : selectedContract ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Service Dialog */}
      <Dialog open={createServiceDialogOpen} onOpenChange={setCreateServiceDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              <Wrench className="w-5 h-5 inline mr-2" />
              Create Service for Contract
            </DialogTitle>
          </DialogHeader>
          
          {selectedContract && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-4">
              <p className="text-sm font-medium text-blue-800">
                Contract: {selectedContract.contract_no} ({selectedContract.contract_type})
              </p>
              <p className="text-xs text-blue-700">
                {selectedContract.machine_device} | 
                Services: {selectedContract.services_used || 0} / {selectedContract.services_included}
                {selectedContract.contract_type === 'AMC' && selectedContract.end_date && (
                  <> | Ends: {formatIndianDateOnly(selectedContract.end_date)}</>
                )}
                {selectedContract.contract_type === 'MMC' && selectedContract.monthly_service_day && (
                  <> | Service Day: {selectedContract.monthly_service_day}</>
                )}
              </p>
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <Label>Customer *</Label>
              <Select
                value={serviceForm.customer_id}
                onValueChange={(v) => setServiceForm(prev => ({ ...prev, customer_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.company_name} - {c.mobile}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Service Type *</Label>
                <Select
                  value={serviceForm.service_type}
                  onValueChange={(v) => setServiceForm(prev => ({ ...prev, service_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AMC/MMC Service">AMC/MMC Service</SelectItem>
                    <SelectItem value="Onsite Field Service">Onsite Field Service</SelectItem>
                    <SelectItem value="In-Office Service">In-Office Service</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Priority</Label>
                <Select
                  value={serviceForm.priority}
                  onValueChange={(v) => setServiceForm(prev => ({ ...prev, priority: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Low">Low</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Assign To Engineer *</Label>
                <Select
                  value={serviceForm.assigned_to || 'none'}
                  onValueChange={(v) => setServiceForm(prev => ({ ...prev, assigned_to: v === 'none' ? '' : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select engineer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- Select Engineer --</SelectItem>
                    {engineers.map(e => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={serviceForm.due_date}
                  onChange={(e) => setServiceForm(prev => ({ ...prev, due_date: e.target.value }))}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>
            
            <div>
              <Label>Issue Description *</Label>
              <Textarea
                value={serviceForm.issue_description}
                onChange={(e) => setServiceForm(prev => ({ ...prev, issue_description: e.target.value }))}
                rows={5}
                placeholder="Service details..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateServiceDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateService} disabled={submitting} data-testid="submit-create-service-btn">
              {submitting ? 'Creating...' : 'Create Service'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ExportPasswordDialog {...protectedExport} />
    </div>
  );
};

export default ContractsManager;
